/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Icon, IgnoredChecksBreakdown, Input, ModalFactory, SplitChecksToggleBreakdown, Tag, Text, ToggleableBreakdown } from 'components';
import { theme, toRem } from 'design';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { ICurrentEmployee, IEmployerState } from 'store/types';
import { DisbursementMethod, ICalculatorRules, IEmployee, IPolicy, IPOSSegment, ISiteIntegration, ISiteRolePolicy, ITipCalculatorPayee, ITipCalculatorResultEmployee } from 'types';
import { buildEmployeeOverrideTicketBreakdown, centsToDollarsDisplay, getRolePolicy, sortAlphaNumerically } from 'utils';
import { roleSiteDisplay } from 'utils/employee';
import ConnectEmployeeModal from '../../modals/ConnectEmployeeModal/ConnectEmployeeModal';
import HoldbackModal from '../../modals/HoldbackModal/HoldbackModal';
import UserInfo from '../../UserInfo/UserInfo';

interface IPosition {
  top: number;
  left: number;
}

export interface IEmployeeListItemProps {
  segments: IPOSSegment[];
  currentCalculatorRules?: ICalculatorRules;
  className?: string;
  currentEmployee: ICurrentEmployee;
  currentEmployeePolicy?: IPolicy;
  currentOrg?: IEmployerState;
  rolePolicy?: ISiteRolePolicy;
  dataCy?: string;
  defaultOpenState?: boolean;
  holdback?: number | null;
  integration?: ISiteIntegration;
  isLoading?: boolean;
  maxTipAmount?: number;
  note?: string;
  onHoldbackChange?: (holdback: number) => void;
  onInvited?: (employee: IEmployee) => void;
  onNoteChange?: (note: string) => void;
  payee: ITipCalculatorPayee;
  creditCardFee?: number;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding-bottom: ${toRem(16)};
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;

  &:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: ${toRem(16)} ${toRem(16)} 0;
`;

const BottomRow = styled(motion.div)`
  display: flex;
  flex-flow: column;
  justify-content: stretch;
  padding: 0 ${toRem(16)};
  overflow: hidden;
`;

const Breakdowns = styled.div`
  display: flex;
  flex-flow: column;

  > div:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

const DisbursementMethodWrapper = styled.div`
  --disbursement-method-wrapper-size: ${toRem(75)};

  display: flex;
  justify-content: flex-end;
  min-width: var(--disbursement-method-wrapper-size);
  width: var(--disbursement-method-wrapper-size);
  max-width: var(--disbursement-method-wrapper-size);
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(8)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;
`;

const EditHoldbackTrigger = styled.span`
  margin-left: ${toRem(4)};
  font-size: ${toRem(11)} !important;
  font-weight: ${theme.font.weight.semiBold};
  color: ${theme.color.primary[600]};
`;

const EmployeeInfo = styled(UserInfo)`
  width: calc(100% - ${toRem(40)}) !important;
  max-width: calc(100% - ${toRem(40)});
`;

const EmployeeInfoWrapper = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: calc(100% - ${toRem(75)});
  margin-right: ${toRem(8)};

  @media (min-width: 1330px) {
    flex-grow: 1;
    max-width: ${toRem(400)};
  }
`;

const EmployeeLaborTipContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  align-items: center;
`;

const ErrorRow = styled.div`
  display: flex;
  padding: ${toRem(8)} ${toRem(20)};
  border-top-right-radius: ${toRem(11)};
  border-top-left-radius: ${toRem(11)};
  justify-content: center;
  text-align: center;

  &.error-0 {
    background-color: ${theme.color.neutral[200]};
  }

  &.error-1,
  &.error-2 {
    background-color: ${theme.color.warning.light};
  }
`;

const ErrorList = styled.ul`
  list-style: none;
`;

const ErrorListItem = styled.li`
  &:not(:first-child) {
    padding-top: ${toRem(8)};
  }
`;

const InlineButton = styled.button`
  display: inline-block;
  padding: 0;
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  background: none;
  border: none;
  color: ${theme.color.primary[600]};
  outline: none;
  margin-left: ${toRem(4)};
`;

const Label = styled.div`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.medium} !important;
  color: ${theme.color.neutral[500]};
  margin-bottom: ${toRem(4)};
`;

const NoteInput = styled(Input)`
  flex-shrink: 0;
`;

const NoteWrapper = styled.div`
  margin-top: ${toRem(16)};
  padding-top: ${toRem(16)};
  border-top: 1px solid ${theme.color.neutral[300]};
`;

const StyledTag = styled(Tag)`
  white-space: nowrap;
`;

const StyledText = styled(Text)`
  font-size: ${toRem(14)};
`;

const TotalsGrid = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
  margin: ${toRem(16)} 0 0;
  padding: 0 ${toRem(24)};

  &.dt-totals-grid {
    display: none;
  }

  p {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.medium};
  }

  @media (min-width: 1330px) {
    margin: 0;

    &.m-totals-grid {
      display: none;
    }

    &.dt-totals-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      min-width: ${toRem(280)};
      grid-gap: ${toRem(16)};
    }
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: column;

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
    white-space: nowrap;
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
  }

  & button {
    position: relative;
    top: ${toRem(-0.5)};
  }

  & button span {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }
`;

const EmployeeListItem: React.FC<IEmployeeListItemProps> = ({
  segments,
  currentCalculatorRules,
  className = '',
  currentEmployee,
  currentEmployeePolicy,
  currentOrg,
  rolePolicy,
  dataCy = 'employee-tips-payout-list-item',
  defaultOpenState,
  holdback,
  integration,
  isLoading,
  maxTipAmount,
  note,
  payee,
  onHoldbackChange,
  onInvited,
  onNoteChange,
  creditCardFee = 0,
}) => {
  const [open, setOpen] = useState(!!defaultOpenState);
  const currentSite = useSelector(selectCurrentEmployeeSite);

  const onTriggerClick = () => {
    setOpen(!open);
  };

  const renderSharedBreakdown = useMemo(
    () =>
      payee.calculations
        .filter((c) => c.received.length || c.contributions.length || c.retained?.total > 0)
        .map((calculation: ITipCalculatorResultEmployee, i) => {
          // Find the segment name by the segment ID in the calculations
          const segment = segments.find((segment) => segment.id === calculation.segmentId);

          return (
            <ToggleableBreakdown
              calculatorRules={currentCalculatorRules.rules}
              categoryGroups={currentCalculatorRules.categoryGroups}
              calculation={calculation}
              creditCardFee={creditCardFee}
              key={`${calculation.segmentId}_${i}`}
              segmentName={segment?.name}
            />
          );
        }),
    [payee]
  );

  // ConnectEmployeeModal
  const [showConnectEmployeeModal, hideConnectEmployeeModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideConnectEmployeeModal} isActive closeOnOutSideClick={false}>
        <ConnectEmployeeModal onClose={hideConnectEmployeeModal} provider={payee.provider} integration={integration} onSuccess={onInvited} organization={currentOrg} />
      </ModalFactory>
    ),
    [payee, integration, currentOrg, onInvited]
  );

  const renderErrors = () => {
    const errors: ReactNode[] = [];
    const instantDisbursement = payee?.employee?.disbursementMethod === DisbursementMethod.INSTANT;

    // show error if net is 0.
    if (payee.net === 0)
      errors.push(
        <StyledText key="zero-net-error" size="small" fontWeight="medium">
          Net must be greater than $0.00, please adjust tip amount {instantDisbursement ? 'or holdback ' : ''}to resolve.
        </StyledText>
      );

    // show error if net is less than 0.
    if (payee.net < 0)
      errors.push(
        <StyledText key="negative-net-error" size="small" fontWeight="medium">
          Negative tip amount, please adjust holdback to resolve.
        </StyledText>
      );

    // show error if payee's tip amount is greater than site max tip amount
    if (payee.net > maxTipAmount) {
      errors.push(
        <StyledText key="max-net-error" size="small" fontWeight="medium">
          Tip amount exceeds this site's maximum tip amount of {centsToDollarsDisplay(maxTipAmount)}, please adjust tip amount {instantDisbursement ? 'or holdback ' : ''}to resolve.
        </StyledText>
      );
    }

    if (!payee?.employee && !!payee?.provider && !!currentSite?.id) {
      // only show this error if there is no user associated with the payee and there is a currentSiteId that has been passed.
      const text =
        currentSite.role === 'admin' ? (
          <StyledText size="small" fontWeight="medium">
            User setup not complete.
            <InlineButton onClick={() => showConnectEmployeeModal()}> Click here to complete setup.</InlineButton>
          </StyledText>
        ) : (
          <StyledText size="small" fontWeight="medium">
            User setup not complete. Contact your site admin to complete setup.
          </StyledText>
        );

      errors.push(
        <ErrorListItem key="no-match-found-error" data-cy="error-match-not-found">
          {text}
        </ErrorListItem>
      );
    }

    if (payee?.employee) {
      const currentPolicy = getRolePolicy(rolePolicy, payee.employee.type, payee.employee.role);
      if (currentPolicy) {
        let policyErrorMsg;

        if (payee.employee.id === currentEmployee.id && !currentPolicy.paySelf) {
          policyErrorMsg = 'You are not eligible to receive tips.';
        } else if (!currentPolicy.payable) {
          policyErrorMsg = `This user role (${roleSiteDisplay(payee.employee.role)}) is ineligible to receive tips.`;
        }

        if (policyErrorMsg) {
          errors.push(
            <ErrorListItem key="ineligible-payee-error" data-cy="error-ineligible-payee">
              <StyledText size="small" fontWeight="medium">
                {policyErrorMsg}
              </StyledText>
            </ErrorListItem>
          );
        }
      }
    }

    // check if any user has been auto clocked out
    const autoClockOut = payee.provider?.schedules.some((item) => item.clockedOut === false);

    if (!errors.length && !autoClockOut) return null;

    // display the auto clockout as a warning (in grey)
    // when no other errors need to be displayed
    if (!errors.length && autoClockOut) {
      return (
        <ErrorRow className="error-0" data-cy="employee-labor-data-item-error">
          <ErrorList>
            <ErrorListItem key="auto-clockout-error" data-cy="error-auto-clockout">
              <StyledText size="small" fontWeight="medium">
                Auto clock out detected. Please verify time worked.
              </StyledText>
            </ErrorListItem>
          </ErrorList>
        </ErrorRow>
      );
    }

    // display the auto clockout as a error (in yellow)
    // when errors need to be displayed
    if (errors.length && autoClockOut) {
      errors.push(
        <ErrorListItem key="auto-clockout-error" data-cy="error-auto-clockout">
          <StyledText size="small" fontWeight="medium">
            Auto clock out detected. Please verify time worked.
          </StyledText>
        </ErrorListItem>
      );
    }

    return (
      <ErrorRow className="error-1" data-cy="employee-labor-data-item-error">
        <ErrorList>{errors}</ErrorList>
      </ErrorRow>
    );
  };

  const renderTotalsGrid = (className: string) => (
    <TotalsGrid className={className}>
      <TotalWrapper data-cy="calculated-tips-total">
        <Label>Calculated Tips</Label>
        {isLoading ? <Skeleton width={90} /> : <p>{centsToDollarsDisplay(payee.gross)}</p>}
      </TotalWrapper>

      {typeof holdback === 'number' && (
        <>
          <TotalWrapper data-cy="holdback-total">
            <Label>Holdback</Label>
            {isLoading ? (
              <Skeleton width={90} />
            ) : (
              <p>
                -{centsToDollarsDisplay(payee.holdbackAmount)}
                {payee?.employee?.disbursementMethod && payee.employee?.disbursementMethod !== DisbursementMethod.PAYROLL && (
                  <HoldbackModal defaultValue={holdback} onChange={onHoldbackChange} payee={payee} disabled={!currentEmployeePolicy?.holdbackAdjustment}>
                    <EditHoldbackTrigger>Edit</EditHoldbackTrigger>
                  </HoldbackModal>
                )}
              </p>
            )}
          </TotalWrapper>

          <TotalWrapper data-cy="net-total">
            <Label>Net</Label>
            {isLoading ? <Skeleton width={90} /> : <p>{centsToDollarsDisplay(payee.net)}</p>}
          </TotalWrapper>
        </>
      )}
    </TotalsGrid>
  );

  const renderOverrideBreakdown = useMemo(() => {
    const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(payee.calculations);

    return (
      <>
        {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && <SplitChecksToggleBreakdown splitChecks={splitChecks} />}
        {!!ignoredChecks.length && <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />}
      </>
    );
  }, [payee]);

  return (
    <Layout data-cy={dataCy} className={className} key={`payee-${payee?.id}`}>
      {renderErrors()}
      <TopRow className="top-row">
        <EmployeeInfoWrapper onClick={onTriggerClick}>
          <DropdownIconWrapper data-cy={`${dataCy}-accordion-trigger`}>
            <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
          </DropdownIconWrapper>
          <EmployeeInfo className="employee-info" hideAvatar isLoading={isLoading} username={payee?.name} />
        </EmployeeInfoWrapper>
        <EmployeeLaborTipContainer>
          {renderTotalsGrid('dt-totals-grid')}
          <DisbursementMethodWrapper>
            {payee?.employee?.disbursementMethod && (
              <StyledTag appearance={payee.employee?.disbursementMethod} label={payee.employee?.disbursementMethod} className={payee?.employee?.disbursementMethod} dataCy="employee-disbursement-method" />
            )}
          </DisbursementMethodWrapper>
        </EmployeeLaborTipContainer>
      </TopRow>
      {renderTotalsGrid('m-totals-grid')}
      <BottomRow initial={open} animate={{ height: open ? 'auto' : 0, marginTop: open ? toRem(16) : 0 }} transition={theme.transition.spring.accordion} data-cy={`${dataCy}-bottom-row`}>
        <Breakdowns>
          {renderSharedBreakdown}
          {renderOverrideBreakdown}
        </Breakdowns>
        <NoteWrapper>
          <NoteInput
            type="text"
            data-cy={`${dataCy}-note-input`}
            maxLength={100}
            placeholder="Enter an optional note."
            defaultValue={note}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              onNoteChange?.(e.currentTarget.value);
            }}
          />
        </NoteWrapper>
      </BottomRow>
    </Layout>
  );
};

export default EmployeeListItem;
